/**
 * BankStorageService - localStorage access for bank data.
 *
 * Provides storage for character bank items with the same
 * localStorage-first pattern used by CharacterStore.
 */

import type { BankData, BankItem } from '@idle-depths/shared';

/**
 * Storage key prefix for bank data.
 */
const BANK_STORAGE_KEY = 'idle-depths:bank';

/**
 * BankStorageService provides typed access to bank storage.
 *
 * All methods are static since localStorage is a singleton.
 */
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class BankStorageService {
  /**
   * Get bank items for a character.
   *
   * @param characterId - Character ID
   * @returns Array of bank items
   */
  static getBank(characterId: string): BankItem[] {
    const key = `${BANK_STORAGE_KEY}_${characterId}`;

    try {
      const data = localStorage.getItem(key);
      if (!data) {
        return [];
      }

      const bankData: BankData = JSON.parse(data) as BankData;
      return bankData.items;
    } catch (error) {
      console.error('Failed to read bank data:', error);
      return [];
    }
  }

  /**
   * Save bank items for a character.
   *
   * @param characterId - Character ID
   * @param items - Bank items to save
   */
  static setBank(characterId: string, items: BankItem[]): void {
    const key = `${BANK_STORAGE_KEY}_${characterId}`;

    try {
      const bankData: BankData = {
        characterId,
        items,
        updatedAt: Date.now(),
        lastSyncedAt: null,
        needsSync: true,
      };

      localStorage.setItem(key, JSON.stringify(bankData));
    } catch (error) {
      console.error('Failed to save bank data:', error);
    }
  }

  /**
   * Get full bank data (including metadata).
   *
   * @param characterId - Character ID
   * @returns Bank data or null if not found
   */
  static getBankData(characterId: string): BankData | null {
    const key = `${BANK_STORAGE_KEY}_${characterId}`;

    try {
      const data = localStorage.getItem(key);
      if (!data) {
        return null;
      }

      return JSON.parse(data) as BankData;
    } catch (error) {
      console.error('Failed to read bank data:', error);
      return null;
    }
  }

  /**
   * Update bank sync metadata.
   *
   * @param characterId - Character ID
   * @param syncedAt - Timestamp of sync
   */
  static markSynced(characterId: string, syncedAt: number): void {
    const key = `${BANK_STORAGE_KEY}_${characterId}`;

    try {
      const data = localStorage.getItem(key);
      if (!data) {
        return;
      }

      const bankData: BankData = JSON.parse(data) as BankData;
      bankData.lastSyncedAt = syncedAt;
      bankData.needsSync = false;

      localStorage.setItem(key, JSON.stringify(bankData));
    } catch (error) {
      console.error('Failed to update bank sync status:', error);
    }
  }

  /**
   * Clear bank data for a character.
   *
   * @param characterId - Character ID
   */
  static clearBank(characterId: string): void {
    const key = `${BANK_STORAGE_KEY}_${characterId}`;

    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to clear bank data:', error);
    }
  }

  /**
   * Check if character has bank data.
   *
   * @param characterId - Character ID
   * @returns True if bank data exists
   */
  static hasBank(characterId: string): boolean {
    const key = `${BANK_STORAGE_KEY}_${characterId}`;

    try {
      return localStorage.getItem(key) !== null;
    } catch {
      return false;
    }
  }
}
